import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useCan } from "@/contexts/PermissionContext";
import type { Action, CanParams } from "@/types/permissions";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import type { PermissionsResponse } from "@/types/permissions";
import { toast } from "react-toastify";

type Props = {
  resource?: string | number;
  action?: Action;
  subResource?: string | number;
  children: React.ReactNode;
  redirectTo?: string;
  allOf?: CanParams[];
  anyOf?: CanParams[];
  showToast?: boolean; // If true, show toast instead of redirecting
};

const RequirePermission: React.FC<Props> = ({
  resource,
  action,
  subResource,
  children,
  redirectTo = "/not-authorized",
  allOf,
  anyOf,
  showToast = false,
}) => {
  const { has, hasAll, hasAny } = useCan();
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false);

  const isFetchingPermissions = useIsFetching({
    predicate: (q) =>
      Array.isArray(q.queryKey) && q.queryKey[0] === "user_permissions",
  });

  const queryClient = useQueryClient();
  const queries = queryClient.getQueriesData<PermissionsResponse>({
    predicate: (q) =>
      Array.isArray(q.queryKey) && q.queryKey[0] === "user_permissions",
  });
  const hasPermissionData = queries.some(([, data]) => !!data);

  // Also check if we have cached app data
  const appData = queryClient.getQueryData(["app-initial-data"]) as {
    permissions: PermissionsResponse;
  } | undefined;
  const hasCachedPermissions = !!appData?.permissions;

  // Check if user is authenticated
  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    // If no token, immediately redirect to login
    if (!token) {
      setIsReady(true);
      return;
    }

    // Only wait for the initial load - once we've checked, we're done waiting
    if (hasCheckedOnce) {
      return; // Already checked, don't update state again
    }

    // Consider ready if we have cached permissions OR if fetching is done
    if (hasCachedPermissions || hasPermissionData || isFetchingPermissions === 0) {
      setIsReady(true);
      setHasCheckedOnce(true);
    }
  }, [isFetchingPermissions, hasPermissionData, hasCachedPermissions, token, hasCheckedOnce]);

  // Show loading spinner while permissions are being fetched
  if (!isReady) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-sm text-gray-600">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // If no token, redirect to login
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Check permissions
  let allowed = true;
  let permissionCheckPerformed = false;

  if (allOf && allOf.length > 0) {
    allowed = hasAll(allOf);
    permissionCheckPerformed = true;
  } else if (anyOf && anyOf.length > 0) {
    allowed = hasAny(anyOf);
    permissionCheckPerformed = true;
  } else if (resource != null && action != null) {
    allowed = has({ resource, action, subResource });
    permissionCheckPerformed = true;
  } else {
    // No permission requirements specified
    allowed = true;
  }

  // If we don't have permission data and a check was needed, deny access
  if (permissionCheckPerformed && !hasPermissionData && !hasCachedPermissions) {
    console.warn("🚫 Permission data not available:", {
      resource,
      action,
      subResource,
      path: location.pathname,
    });
    allowed = false;
  }

  // Only log permission denials to reduce console noise
  if (!allowed) {
    console.warn("🚫 Permission denied:", {
      resource,
      action,
      subResource,
      allOf,
      anyOf,
      path: location.pathname,
      hasPermissionData,
      hasCachedPermissions,
    });
  }

  if (!allowed) {
    console.log("value from requirePermission");

    // If showToast is true, show a toast notification and stay on the current page
    if (showToast) {
      toast.error("You don't have permission to access this feature. Please contact your administrator.");
      return <>{children}</>; // Still render children but they should be locked/disabled
    }

    const target = redirectTo || "/not-authorized";
    const safeTarget = location.pathname === target ? "/dashboard" : target;
    return <Navigate to={safeTarget} replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

export default RequirePermission;
